import * as Sentry from "@sentry/tanstackstart-react";

export const SENTRY_TUNNEL_PATH = "/api/monitoring";

interface SentryScopeUser {
  email?: string | null;
}

interface SentryScopeSchool {
  slug: string;
}

/**
 * Sync the signed-in user and active school onto the browser Sentry scope so errors and
 * transactions are filterable per school and count affected users in GlitchTip. Email doubles as
 * the user id (the backend exposes no user id to the client; OpenPanel identifies by email too).
 * Impersonated sessions carry the impersonated user plus an `impersonated` tag, mirroring the
 * OpenPanel property. Unlike the OpenPanel group, the school tag is cleared outside school routes
 * — an error on the school-select or admin pages must not be attributed to the last visited school.
 */
export function syncSentryScope(
  user: SentryScopeUser | null,
  school: SentryScopeSchool | null,
  options?: { impersonated?: boolean },
) {
  setSentryUser(user?.email);
  Sentry.setTag("school.slug", school?.slug);
  // Undefined removes the tag, so regular sessions don't carry impersonated:false.
  Sentry.setTag("impersonated", options?.impersonated === true ? "true" : undefined);
}

/**
 * Email doubles as the Sentry user id. On the server this runs per request (see
 * `sentryUserMiddleware`, which also passes the client IP), and the pre-login server functions
 * call it with the submitted email so a failed login attempt is searchable by user too; the IP
 * already on the scope survives that call. The SDK copies the scope user onto every log line
 * (`user.email`) and event.
 */
export function setSentryUser(email: string | null | undefined, ipAddress?: string) {
  const ip = ipAddress ?? Sentry.getIsolationScope().getUser()?.ip_address;
  if (!email && !ip) {
    Sentry.setUser(null);
    return;
  }
  const user: Sentry.User = {};
  if (email) {
    user.id = email;
    user.email = email;
  }
  if (ip) user.ip_address = ip;
  Sentry.setUser(user);
}
